import { useState } from 'react';
import { toast } from 'sonner';

import { productionApi } from 'api/production';
import { TOAST_ERROR_STYLE } from 'components/production/components/AisleContent/constants';
import { Card, CardContent, CardHeader, CardTitle } from 'shadcn/components/ui/Card';

import BannerEditorActions from './BannerEditorActions';
import BannerEditorForm from './BannerEditorForm';
import BannerEditorSummary from './BannerEditorSummary';

/**
 * Displays an editor for aisle banners.
 *
 * @param {string} [aisleID] - Parent aisle ID.
 * @param {Object} banner - Aisle banner data.
 * @param {Object} brandOptions - Brand options.
 * @param {Function} handleDeleteNewEditor - Callback function to delete a new banner editor.
 * @param {string} initialView - Initial view, defaults to 'summary'.
 * @param {Function} refetchBanners - Function to refetch banners from the parent component.
 */
export default function BannerEditor({
    aisleID,
    banner,
    brandOptions,
    handleDeleteNewEditor,
    initialView = 'summary',
    refetchBanners,
}) {
    const [view, setView] = useState(initialView);
    const [formData, setFormData] = useState(banner);
    const [imageErrors, setImageErrors] = useState({
        largeImage: null,
        smallImage: null,
    });
    const [isSaving, setIsSaving] = useState(false);

    const toggleView = () => {
        setView((prev) => (prev === 'summary' ? 'form' : 'summary'));
    };

    const handleChange = (e) => {
        setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const handleImageClear = () => {
        setFormData((prev) => ({ ...prev, imageUrl: '' }));
    };

    const handleImageUpload = async (file, imageType) => {
        try {
            const response = await productionApi.uploadStorefrontConfigImage(file);
            const imageUrl = response.data.imagePath;
            setFormData((prev) => ({ ...prev, [imageType]: imageUrl, [`${imageType}File`]: file }));
            setImageErrors({ ...imageErrors, [imageType]: null });
        } catch (e) {
            setImageErrors({ ...imageErrors, [imageType]: `Error: ${e.message}` });
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setIsSaving(true);
        const loadingToastID = toast.loading('Saving banner...');

        // The new Lettuce UI expects filenames for display, while the existing
        // banner logic still relies on binary files. We're supporting both formats
        // for now, until the endpoint is updated to accept filenames.
        try {
            if (banner.id) {
                // Update existing banner
                const { largeImage, largeImageFile, smallImage, smallImageFile, taxonomyID, ...restFormData } =
                    formData;
                const response = await productionApi.updateAisleBanner({
                    id: banner.id,
                    ...restFormData,
                    ...(largeImageFile && { largeImage: largeImageFile }),
                    ...(smallImageFile && { smallImage: smallImageFile }),
                    active: formData.active ? 'Yes' : 'No',
                    aisleID,
                });

                if (!response.status) {
                    throw new Error('Failed to update banner');
                }
            } else {
                // Create new banner
                const { largeImageFile, smallImageFile, ...restFormData } = formData;
                const response = await productionApi.createAisleBanner({
                    ...restFormData,
                    active: formData.active ? 'Yes' : 'No',
                    aisleID,
                    largeImage: largeImageFile,
                    smallImage: smallImageFile,
                });

                if (!response.status) {
                    throw new Error('Failed to create banner');
                }

                handleDeleteNewEditor();
            }
            toast.success('Banner saved successfully');
        } catch (error) {
            console.error(error);
            toast.error(error.message, {
                style: TOAST_ERROR_STYLE,
            });
        } finally {
            setIsSaving(false);
            toast.dismiss(loadingToastID);
            refetchBanners();
        }
    };

    const handleDelete = async () => {
        if (!banner.id) {
            handleDeleteNewEditor();
            return;
        }

        setIsSaving(true);
        const loadingToastID = toast.loading('Deleting banner...');

        try {
            const response = await productionApi.deleteAisleBanner(banner.id);
            if (!response.status) {
                throw new Error(response.msg || 'Failed to delete banner');
            }
            toast.success('Banner deleted successfully');
        } catch (error) {
            console.error(error);
            toast.error(error.message, {
                style: TOAST_ERROR_STYLE,
            });
        } finally {
            setIsSaving(false);
            toast.dismiss(loadingToastID);
            refetchBanners();
        }
    };

    return (
        <Card>
            <CardHeader>
                <CardTitle className="flex items-center justify-between">
                    {banner.bannerName ? (
                        <div className="flex items-center gap-1">
                            <h3>{banner.bannerName}</h3>
                            <span className="ml-2 text-sm text-muted-foreground">(H1 Banner)</span>
                            <span className="ml-2 font-normal text-sm text-muted-foreground">id: {banner.id}</span>
                        </div>
                    ) : (
                        <h3>H1 Banner</h3>
                    )}
                    <BannerEditorActions onDelete={handleDelete} toggleView={toggleView} view={view} />
                </CardTitle>
            </CardHeader>
            <CardContent>
                {view === 'summary' && (
                    <BannerEditorSummary data={banner} formData={formData} toggleView={toggleView} />
                )}
                {view === 'form' && (
                    <BannerEditorForm
                        aisleID={aisleID}
                        brandOptions={brandOptions}
                        data={banner}
                        formData={formData}
                        handleChange={handleChange}
                        handleImageClear={handleImageClear}
                        handleImageUpload={handleImageUpload}
                        handleSave={handleSave}
                        imageErrors={imageErrors}
                        isSaving={isSaving}
                    />
                )}
            </CardContent>
        </Card>
    );
}
